import { Extension } from '@tiptap/core'
import { PluginKey } from '@tiptap/pm/state'
import { ReactRenderer } from '@tiptap/react'
import Suggestion from '@tiptap/suggestion'
import tippy from 'tippy.js'
import { ActionPillMenuList } from './action-pill-menu-list'

const extensionName = 'actionPillSuggestion'
let popup: any

function getScrollParents(node: HTMLElement | null): Array<HTMLElement | Window> {
  const parents: Array<HTMLElement | Window> = [window]
  let current = node?.parentElement ?? null

  while (current) {
    const { overflowY, overflow } = window.getComputedStyle(current)
    const isScrollable = /(auto|scroll|overlay)/.test(`${overflow} ${overflowY}`)
    if (isScrollable) {
      parents.push(current)
    }
    current = current.parentElement
  }

  return parents
}

export interface ActionPillSuggestionItem {
  /** Integration check id — present for check-based items. */
  checkId?: string
  /** Agent tool id in "namespace.action" form — present for tool-based items. */
  tool?: string
  provider: string
  providerLabel: string
  action: string
  name: string
  description: string
  connected: boolean
}

function wordsFromAction(value: string): string[] {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_./:-]+/g, ' ')
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.trim())
    .filter(Boolean)
}

function toCamelCase(words: string[]): string {
  if (words.length === 0) return ''
  const [first, ...rest] = words
  return [
    first,
    ...rest.map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`),
  ].join('')
}

function singularize(word: string): string {
  if (word.endsWith('ies')) return `${word.slice(0, -3)}y`
  if (word.endsWith('ses')) return word.slice(0, -2)
  if (word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1)
  return word
}

function defaultOutputVarForAction(item: ActionPillSuggestionItem): string {
  const source = item.action || item.name || item.tool || item.checkId || 'result'
  const words = wordsFromAction(source)
  const [verb, ...objectWords] = words

  if (!verb) return 'result'

  if (['list', 'search'].includes(verb) && objectWords.length > 0) {
    return toCamelCase(objectWords)
  }

  if (['get', 'fetch', 'find', 'lookup', 'read'].includes(verb) && objectWords.length > 0) {
    const [head, ...tail] = objectWords
    return toCamelCase([singularize(head), ...tail])
  }

  if (objectWords.length > 0) {
    return `${toCamelCase([verb, ...objectWords])}Result`
  }

  return `${verb}Result`
}

function collectExistingOutputVars(doc: any): Set<string> {
  const outputVars = new Set<string>()

  doc.descendants((node: any) => {
    if (node.type?.name !== 'actionPill') return
    const outputVar = node.attrs?.outputVar
    if (typeof outputVar === 'string' && outputVar.trim().length > 0) {
      outputVars.add(outputVar.trim())
    }
  })

  return outputVars
}

function uniqueOutputVar(base: string, existing: Set<string>): string {
  const fallback = base.trim() || 'result'
  if (!existing.has(fallback)) return fallback

  let suffix = 2
  while (existing.has(`${fallback}${suffix}`)) {
    suffix += 1
  }
  return `${fallback}${suffix}`
}

/**
 * TipTap extension that triggers a provider → action picker when the user types `#`.
 * Selected actions are inserted as actionPill inline nodes.
 *
 * The `items` callback is provided via configuration so the consuming component
 * can plug in a tRPC-backed search function.
 */
export const ActionPillSuggestion = Extension.create<{
  getItems: (query: string) => Promise<ActionPillSuggestionItem[]> | ActionPillSuggestionItem[]
}>({
  name: extensionName,

  priority: 200,

  addOptions() {
    return {
      getItems: () => [],
    }
  },

  onCreate() {
    popup = tippy('body', {
      interactive: true,
      trigger: 'manual',
      placement: 'bottom-start',
      theme: 'slash-command',
      maxWidth: '20rem',
      offset: [16, 8],
      popperOptions: {
        strategy: 'fixed',
        modifiers: [{ name: 'flip', enabled: false }],
      },
    })
  },

  onDestroy() {
    popup?.[0]?.destroy()
    popup = null
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        char: '#',
        // The action catalog has many multi-word names ("Branch Protection
        // Rules", "Force Push Disabled"). Allow spaces inside the query so
        // the popup stays open as the user types a phrase. Esc closes it,
        // Enter / click commits a selection.
        allowSpaces: true,
        startOfLine: false,
        allowedPrefixes: null,
        pluginKey: new PluginKey(extensionName),
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from)
          const isParagraph = $from.parent.type.name === 'paragraph'
          const isListItem =
            this.editor.isActive('listItem') ||
            this.editor.isActive('taskItem')
          return isParagraph || isListItem
        },
        items: async ({ query }) => {
          const items = await this.options.getItems(query)

          // Group by provider
          const groups = new Map<
            string,
            { provider: string; providerLabel: string; connected: boolean; actions: ActionPillSuggestionItem[] }
          >()

          for (const item of items) {
            if (!groups.has(item.provider)) {
              groups.set(item.provider, {
                provider: item.provider,
                providerLabel: item.providerLabel,
                connected: item.connected,
                actions: [],
              })
            }
            groups.get(item.provider)!.actions.push(item)
          }

          return Array.from(groups.values())
        },
        command: ({ editor, range, props }: any) => {
          const item = props as ActionPillSuggestionItem
          const outputVar = uniqueOutputVar(
            defaultOutputVarForAction(item),
            collectExistingOutputVars(editor.state.doc)
          )
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertActionPill({
              provider: item.provider,
              action: item.action,
              label: `${item.providerLabel}: ${item.name}`,
              checkId: item.checkId,
              tool: item.tool,
              outputVar,
            })
            .insertContent(' ')
            .run()
        },
        render: () => {
          let component: ReactRenderer | null = null
          let scrollParents: Array<HTMLElement | Window> = []
          let repositionPopup: (() => void) | null = null

          const cleanupListeners = () => {
            if (!repositionPopup) return
            for (const target of scrollParents) {
              target.removeEventListener('scroll', repositionPopup)
            }
            window.removeEventListener('resize', repositionPopup)
            scrollParents = []
            repositionPopup = null
          }

          return {
            onStart: (props: any) => {
              component = new ReactRenderer(ActionPillMenuList, {
                props,
                editor: props.editor,
              })

              if (!props.clientRect) return

              const getReferenceClientRect = () => {
                const rect = props.clientRect?.()
                if (!rect) return new DOMRect(0, 0, 0, 0)

                const menuHeight = component?.element?.offsetHeight ?? 0
                const viewportPadding = 16
                const gap = 8
                let yPos = rect.y

                if (menuHeight > 0) {
                  const bottomEdge = rect.top + menuHeight + gap + viewportPadding
                  if (bottomEdge > window.innerHeight) {
                    const fitsAbove = rect.top - gap - menuHeight >= viewportPadding
                    if (fitsAbove) {
                      yPos = rect.top - menuHeight - gap
                    } else {
                      const overflow = bottomEdge - window.innerHeight
                      yPos = Math.max(viewportPadding, rect.y - overflow)
                    }
                  }
                }

                return new DOMRect(rect.x, yPos, rect.width, rect.height)
              }

              cleanupListeners()
              repositionPopup = () => {
                popup?.[0]?.setProps({
                  getReferenceClientRect,
                })
              }
              scrollParents = getScrollParents(props.editor.view.dom.parentElement)
              for (const target of scrollParents) {
                target.addEventListener('scroll', repositionPopup, { passive: true })
              }
              window.addEventListener('resize', repositionPopup)

              popup?.[0]?.setProps({
                getReferenceClientRect,
                appendTo: () => document.body,
                content: component.element,
              })
              popup?.[0]?.show()
            },
            onUpdate: (props: any) => {
              component?.updateProps(props)
              if (!props.clientRect) return

              const getReferenceClientRect = () => {
                const rect = props.clientRect?.()
                if (!rect) return new DOMRect(0, 0, 0, 0)

                const menuHeight = component?.element?.offsetHeight ?? 0
                const viewportPadding = 16
                const gap = 8
                let yPos = rect.y

                if (menuHeight > 0) {
                  const bottomEdge = rect.top + menuHeight + gap + viewportPadding
                  if (bottomEdge > window.innerHeight) {
                    const fitsAbove = rect.top - gap - menuHeight >= viewportPadding
                    if (fitsAbove) {
                      yPos = rect.top - menuHeight - gap
                    } else {
                      const overflow = bottomEdge - window.innerHeight
                      yPos = Math.max(viewportPadding, rect.y - overflow)
                    }
                  }
                }

                return new DOMRect(rect.x, yPos, rect.width, rect.height)
              }

              cleanupListeners()
              repositionPopup = () => {
                popup?.[0]?.setProps({
                  getReferenceClientRect,
                })
              }
              scrollParents = getScrollParents(props.editor.view.dom.parentElement)
              for (const target of scrollParents) {
                target.addEventListener('scroll', repositionPopup, { passive: true })
              }
              window.addEventListener('resize', repositionPopup)

              popup?.[0]?.setProps({
                getReferenceClientRect,
              })
            },
            onKeyDown: (props: any) => {
              if (props.event.key === 'Escape') {
                popup?.[0]?.hide()
                return true
              }
              return (component?.ref as any)?.onKeyDown?.(props) ?? false
            },
            onExit: () => {
              cleanupListeners()
              popup?.[0]?.hide()
              component?.destroy()
            },
          }
        },
      }),
    ]
  },
})
